import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOSchema from '../../components/feature/SEOSchema';
import { CREDIT_PACKS } from '../../lib/supabase';

const categories = [
  { icon: 'ri-bill-line', label: 'Invoices', color: 'bg-blue-50 text-blue-600', count: '12 templates' },
  { icon: 'ri-file-paper-2-line', label: 'Contracts', color: 'bg-purple-50 text-purple-600', count: '8 templates' },
  { icon: 'ri-user-line', label: 'Resumes', color: 'bg-green-50 text-green-600', count: '10 templates' },
  { icon: 'ri-group-line', label: 'HR Forms', color: 'bg-orange-50 text-orange-600', count: '15 templates' },
  { icon: 'ri-checkbox-line', label: 'Checklists', color: 'bg-teal-50 text-teal-600', count: '9 templates' },
  { icon: 'ri-briefcase-line', label: 'Business Forms', color: 'bg-red-50 text-red-600', count: '14 templates' },
];

const steps = [
  {
    number: '01',
    title: 'Create account',
    description: 'Sign up free and get 100 credits instantly — no credit card required.',
    icon: 'ri-user-add-line',
  },
  {
    number: '02',
    title: 'Use credits',
    description: 'Browse templates, fill in your details, and spend credits to export your documents.',
    icon: 'ri-coin-line',
  },
  {
    number: '03',
    title: 'Download files',
    description: 'Download as PDF or DOCX, ready to send or print in seconds.',
    icon: 'ri-download-2-line',
  },
];

const featuredTemplates = [
  {
    title: 'Professional Invoice',
    category: 'Invoices',
    credits: 25,
    preview: 'https://public.readdy.ai/ai/img_res/c62686f34b5d581f72f28dbc0aa4b3bb.jpg',
  },
  {
    title: 'Service Contract',
    category: 'Contracts',
    credits: 25,
    preview: 'https://readdy.ai/api/search-image?query=professional%20legal%20service%20contract%20document%20template%20with%20signature%20lines%20formal%20layout%20clean%20white%20minimal%20design&width=400&height=280&seq=tpl2&orientation=landscape',
  },
  {
    title: 'Modern Resume',
    category: 'Resumes',
    credits: 25,
    preview: 'https://readdy.ai/api/search-image?query=modern%20professional%20resume%20cv%20template%20clean%20minimal%20design%20two%20column%20layout%20elegant%20typography%20white%20background&width=400&height=280&seq=tpl3&orientation=landscape',
  },
];

const HomePage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://workflowtools.com';

  const schema = useMemo(() => [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'WorkflowTools',
      url: siteUrl,
      description: 'Professional document templates — invoices, contracts, resumes, HR forms, and business templates ready to download instantly.',
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteUrl}/templates?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'WorkflowTools',
      url: siteUrl,
      logo: `${siteUrl}/vite.svg`,
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'support@workflowtools.com',
        contactType: 'customer support',
      },
      sameAs: [
        'https://twitter.com/workflowtools',
        'https://linkedin.com/company/workflowtools',
      ],
    },
  ], [siteUrl]);

  return (
    <div className="min-h-screen bg-white">
      <SEOSchema schema={schema} id="home-schema" />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-full px-4 py-1.5 mb-8">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-coin-line text-amber-600 text-xs"></i>
            </div>
            <span className="text-amber-700 text-sm font-medium">Includes 100 free credits for new users</span>
          </div>

          <h1 className="text-5xl font-bold text-gray-900 leading-tight mb-6">
            Create professional documents<br />
            <span className="text-gray-400">in seconds</span>
          </h1>
          <p className="text-xl text-gray-500 mb-10 max-w-2xl mx-auto leading-relaxed">
            Invoices, contracts, resumes, HR forms, and business templates ready to download instantly.
          </p>

          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link
              to="/signup"
              className="bg-gray-900 text-white font-semibold px-8 py-3.5 rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-base"
            >
              Start Free
            </Link>
            <Link
              to="/templates"
              className="bg-white text-gray-900 font-semibold px-8 py-3.5 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-colors whitespace-nowrap cursor-pointer text-base"
            >
              Browse Templates
            </Link>
          </div>

          <div className="mt-10 flex items-center justify-center gap-8 text-sm text-gray-400">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-line text-green-500 text-sm"></i>
              </div>
              No credit card required
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-line text-green-500 text-sm"></i>
              </div>
              100 free credits
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-line text-green-500 text-sm"></i>
              </div>
              Download instantly
            </div>
          </div>
        </div>

        {/* Hero visual - template previews */}
        <div className="max-w-5xl mx-auto mt-16">
          <div className="grid grid-cols-3 gap-4">
            {featuredTemplates.map((tpl, idx) => (
              <div key={idx} className="rounded-2xl overflow-hidden border border-gray-100 bg-white">
                <div className="w-full h-44 overflow-hidden">
                  <img
                    src={tpl.preview}
                    alt={tpl.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{tpl.title}</p>
                    <p className="text-xs text-gray-400">{tpl.category}</p>
                  </div>
                  <div className="flex items-center gap-1 bg-amber-50 rounded-full px-2.5 py-1">
                    <i className="ri-coin-line text-amber-500 text-xs"></i>
                    <span className="text-amber-700 text-xs font-semibold">{tpl.credits}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Browse by category</h2>
            <p className="text-gray-500">Find the right template for every business need</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {categories.map((cat, idx) => (
              <Link
                key={idx}
                to="/templates"
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-gray-200 transition-all cursor-pointer group"
              >
                <div className={`w-12 h-12 ${cat.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <i className={`${cat.icon} text-xl`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{cat.label}</h3>
                <p className="text-sm text-gray-400">{cat.count}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">How it works</h2>
            <p className="text-gray-500">Get your documents done in three simple steps</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, idx) => (
              <div key={idx} className="relative">
                {idx < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-px border-t-2 border-dashed border-gray-200 -translate-x-1/2"></div>
                )}
                <div className="w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center mb-5">
                  <i className={`${step.icon} text-white text-2xl`}></i>
                </div>
                <div className="text-xs font-bold text-gray-300 mb-2 tracking-widest">{step.number}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Simple credit pricing</h2>
            <p className="text-gray-500">Buy only what you need. Credits never expire.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {CREDIT_PACKS.map((pack) => (
              <div
                key={pack.id}
                className={`bg-white rounded-2xl p-6 border-2 relative ${
                  pack.popular ? 'border-gray-900' : 'border-gray-100'
                }`}
              >
                {pack.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    Most Popular
                  </div>
                )}
                {pack.bestValue && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    Best Value
                  </div>
                )}
                <div className="flex items-center gap-1 mb-3">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-coin-line text-amber-500 text-sm"></i>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{pack.credits >= 1000 ? `${pack.credits / 1000}K` : pack.credits}</span>
                </div>
                <p className="text-gray-400 text-xs mb-4">credits</p>
                <p className="text-2xl font-bold text-gray-900">${pack.price}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/pricing"
              className="inline-block bg-gray-900 text-white font-semibold px-8 py-3.5 rounded-xl hover:bg-gray-800 transition-colors cursor-pointer"
            >
              View All Packs
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-coin-line text-amber-600 text-sm"></i>
            <span className="text-amber-700 text-sm font-medium">Start free with 100 credits</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Ready to get started?</h2>
          <p className="text-gray-500 mb-8">Create your account and get 100 free credits. No credit card required.</p>
          <Link
            to="/signup"
            className="inline-block bg-gray-900 text-white font-semibold px-10 py-4 rounded-xl hover:bg-gray-800 transition-colors cursor-pointer text-base"
          >
            Create Free Account
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
